/* eslint-disable max-lines */
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import ChatStore from 'stores/ChatStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import SwitchActionItem from 'components/ChatSettingsItem/SwitchActionItem';
import ChatTranslationPicker from 'components/ChatTranslationPicker/ChatTranslationPicker';
import FontSizePicker from 'components/FontSizePicker/FontSizePicker';
import ReactSelect from 'components/ReactSelect/ReactSelect';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import allUserGroups from 'enum/userGroups';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';
import { generateID, isMyProfileHelperFunc } from 'utils/helpers';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';

import chatSettingsIcon from '../../../images/chat/settings-icon.png';

import GoldStar from './GoldStar';
import styles from './ChatSettingsV2.css';


@observer
class ChatSettingsV2 extends PureComponent {
  static propTypes = {
    history: PropTypes.object.isRequired,
    toggleDrawer: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    isDesktop: PropTypes.bool,
    setShowRegistrationModal: PropTypes.func,
  };

  static defaultProps = {
    isDesktop: false,
    setShowRegistrationModal: () => undefined,
  };

  constructor(...args) {
    super(...args);

    this.IDs = {
      prefix: 'zVeZdaLe',
      element: 'chatSettings',
      fontOptions: 'fontOptions',
      languages: 'languages',
      disableInvites: 'disableInvites',
      disableUserPm: 'disableUserPm',
      disableBroadcasterPm: 'disableBroadcasterPm',
      translator: 'translator',
      showOriginalLang: 'showOriginalLang',
      chatFilters: 'chatFilters',
      roomEntrance: 'roomEntrance',
      filterLangs: 'filterLangs',
      filterGenders: 'filterGenders',
      visibilityOptions: 'visibilityOptions',
    };
    this.makeId = generateID(this.IDs.prefix, this.IDs.element);
  }

  componentDidMount() {
    const { user: { username } } = this.props;
    ChatStore.getTranslationSettings(username);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user.username !== this.props.user.username) {
      ChatStore.getTranslationSettings(this.props.user.username);
      ChatStore.getChatSettings(this.props.user.username);
      ChatStore.getChatSettings(ProfileStore.profileName, true);
    }
    if (prevProps.user.username !== this.props.user.username && this.props.user.username === null) {
      ChatStore.setLanguage(null);
    }
  }

  handleUpgradeToGold = (type, modalProps) => {
    let area = CONTEXTUAL_GOLD_MODAL_AREAS.TRANSLATION;
    if (type === 'stealthModeValue' || type === 'notifyIEnterRoom') {
      area = CONTEXTUAL_GOLD_MODAL_AREAS.ANONYMOUS_TIPPING;
    }
    ModalService.showModal(
      ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
      { area, ...modalProps }
    );
  };

  showGuestBillingRegistrationModal = () => {
    const { toggleDrawer } = this.props;
    const { isGuestBilling } = UserStore;
    if (isGuestBilling) {
      toggleDrawer();
      ModalService.showModal(
        ModalService.MODAL_KEYS.GUEST_BILLING_MODAL,
        {
          close: ModalService.hideModal,
          show: true,
        }
      );
    }
  };

  showGuestBillingUpgradeGoldModal = () => {
    const { toggleDrawer } = this.props;
    const { isGuestBilling } = UserStore;
    if (isGuestBilling) {
      toggleDrawer();
      this.handleUpgradeToGold();
    }
  };

  handleLanguageSelectChange = (option) => {
    const { user: { username, accessLevel } } = this.props;
    const {
      setLanguage,
      translationSettings: { active, showOriginalLanguage },
    } = ChatStore;

    const data = {
      active,
      languageCode: option.value,
      showOriginalLanguage: Boolean(showOriginalLanguage),
    };

    const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
    setLanguage(option.value);
    if (isGold) {
      ChatStore.saveTranslationSettings(username, data);
    } else {
      this.showGuestBillingRegistrationModal();
    }
  };

  handleTranslationActiveToggle = (e) => {
    const { translationSettings: { languageCode, showOriginalLanguage } } = ChatStore;
    const data = {
      active: e.target.checked,
      languageCode,
      showOriginalLanguage: Boolean(showOriginalLanguage),
    };
    this.updateTranslationSettings(data);
  };

  handleShowOriginalLanguage = (e) => {
    const { translationSettings: { active, languageCode } } = ChatStore;
    const data = {
      active,
      languageCode,
      showOriginalLanguage: e.target.checked,
    };
    this.updateTranslationSettings(data);
  };

  updateTranslationSettings = (data) => {
    const {
      user: { username, accessLevel },
      toggleDrawer,
    } = this.props;
    const { isGuestBilling } = UserStore;

    const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
    if (isGold) {
      ChatStore.saveTranslationSettings(username, data);
    } else if (username && !isGuestBilling) {
      toggleDrawer();
      this.handleUpgradeToGold();
    } else {
      toggleDrawer();
      this.handleUpgradeToGold(null, { onMembershipClick: this.handleGuestOpenGoldOverlay });
    }
  };

  handleGuestOpenGoldOverlay = () => {
    const { history } = this.props;

    ModalService.showModal(
      ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL,
      {
        history,
        onClose: ModalService.hideModal,
        showRegistrationGoldModal: true,
      }
    );
  };

  handleChatFiltersSwitch = (e, filterProperty) => {
    const {
      user: { username, accessHash, accessLevel },
      toggleDrawer,
    } = this.props;
    const { toggleChatFilters } = ChatStore;
    const { profileName } = ProfileStore;
    const { isGuestBilling } = UserStore;

    const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
    if (isGold) {
      const filter = { [filterProperty]: filterProperty === 'stealthModeValue' ? e.value : e.target.checked };
      toggleChatFilters(filter, username, accessHash, profileName);
    } else if (username && !isGuestBilling) {
      toggleDrawer();
      this.handleUpgradeToGold(filterProperty);
    } else {
      toggleDrawer();
      this.handleUpgradeToGold(filterProperty, { onMembershipClick: this.handleGuestOpenGoldOverlay });
    }
  };

  handleChatFiltersSelect = (option, isGenderFilter) => {
    const { user: { username, accessHash } } = this.props;
    const { toggleChatFilters } = ChatStore;
    const { profileName } = ProfileStore;

    let filter = undefined;
    if (isGenderFilter) {
      const allowGenderValue = option.value !== 'unknown';
      filter = { selectedGender: option.value, allowGender: allowGenderValue };
    } else {
      filter = { allowMyLanguage: option.value };
    }

    this.showGuestBillingRegistrationModal();
    toggleChatFilters(filter, username, accessHash, profileName);
  };

  handleNonGoldSwitch = (e, filterName) => {
    const {
      user: { username, accessHash },
      setShowRegistrationModal,
      toggleDrawer,
    } = this.props;
    const { toggleChatFilters } = ChatStore;
    const { profileName } = ProfileStore;
    const { isGuestBilling } = UserStore;

    let filterValue = undefined;
    if (filterName === 'normalScreenFontSize') {
      filterValue = e.font;
    } else if (filterName === 'allowPrivateMessagesFromOtherViewers') {
      filterValue = !e.target.checked;
    } else {
      filterValue = e.target.checked;
    }

    const filter = { [filterName]: filterValue };

    if (username && !isGuestBilling) {
      toggleChatFilters(filter, username, accessHash, profileName);
    } else if (!username && !isGuestBilling) {
      toggleDrawer();
      setShowRegistrationModal(true, RegistrationModalCaller.CHAT_SETTINGS);
    } else {
      this.showGuestBillingRegistrationModal();
    }
  };

  onNormalScreenFontSize = (e) => {
    this.handleNonGoldSwitch(e, 'normalScreenFontSize');
  };

  onLangFilterChange = (e) => {
    this.handleChatFiltersSelect(e, false);
  };

  onGenderFilterChange = (e) => {
    this.handleChatFiltersSelect(e, true);
  };

  onStealthModeChange = (e) => {
    this.handleChatFiltersSwitch(e, 'stealthModeValue');
  };

  render() {
    const {
      user: { accessLevel, username },
      isDesktop,
      user,
    } = this.props;
    const {
      translationSettings: {
        active,
        languageCode,
        showOriginalLanguage,
      },
      chatSettings: {
        allowMyLanguage,
        allowPrivateMessagesFromOtherViewers,
        disablePrivateChat,
        enableFilters,
        normalScreenFontSize,
        notifyIEnterRoom,
        selectedGender,
        stealthModeValue,
        turnOffRoomInvites,
      },
    } = ChatStore;
    const { profileName } = ProfileStore;
    const { hasCF } = UserStore;

    const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
    const translationSettingsAreActive = active && isGold;
    const isBroadcasterAsViewer = (
      hasCF(allUserGroups.BROADCASTER_AS_VIEWER) &&
      isMyProfileHelperFunc(user, profileName)
    );

    const chatSettingsLanguageOptions = [
      { label: translate('v2profile.all.languages'), value: false },
      { label: translate('v2profile.users.speak.my.language'), value: true },
    ];
    const chatSettingsGenderOptions = [
      { label: translate('v2profile.all.genders'), value: 'unknown' },
      { label: translate('v2profile.females'), value: 'female' },
      { label: translate('v2profile.males'), value: 'male' },
      { label: translate('v2profile.trans'), value: 'shemale' },
    ];
    const chatSettingsVisibilityOptions = [
      { label: translate('v2profile.visible.to.everyone'), value: 'visible' },
      { label: translate('v2profile.hide.from.user.list'), value: 'hidden' },
      { label: translate('v2profile.watch.anonymously'), value: 'hidden_anonymous' },
    ];

    const settingsDescription = classNames({
      [styles.settingsDesc]: isGold,
      [styles.settingsDescNotGold]: !isGold,
    });
    const switchWrapper = classNames({ [styles.switchWrapper]: isDesktop });
    const languageAndGenderWrapper = classNames({
      [styles.chatFilterWrapperActive]: active,
      [styles.chatFilterWrapper]: !active,
    });
    const container = classNames({
      [styles.container]: !isDesktop,
      [styles.containerDesktop]: isDesktop,
    });

    return (
      <div className={container}>
        <div className={styles.translatorWrap}>
          <div className={styles.title}>
            {translate('v2profile.chat.settings')}
          </div>
          {isBroadcasterAsViewer &&
            <div className={styles.secondTitle}>
              {translate('v2profile.second.title.broadcast.viewer')}
            </div>
          }
          <FontSizePicker
            id={this.makeId(this.IDs.fontOptions)}
            isDesktop={isDesktop}
            normalScreenFontSize={normalScreenFontSize}
            onChange={this.onNormalScreenFontSize}
          />
          <SwitchActionItem
            label={translate('v2profile.disable.room.invites')}
            labelClassName={styles.privateChatOptionsSubtitle}
            switchProps={{
              id: this.makeId(this.IDs.disableInvites),
              color: 'default',
              checked: Boolean(turnOffRoomInvites),
              value: turnOffRoomInvites,
              onChange: (e) => this.handleNonGoldSwitch(e, 'turnOffRoomInvites'),
              wrapperClassName: switchWrapper,
            }}
            wrapperClassName={styles.privateChatOptions}
          />
          <SwitchActionItem
            label={translate('v2profile.disable.private.messages.users')}
            labelClassName={styles.privateChatOptionsSubtitle}
            switchProps={{
              id: this.makeId(this.IDs.disableUserPm),
              color: 'default',
              checked: username ? !allowPrivateMessagesFromOtherViewers : false,
              value: allowPrivateMessagesFromOtherViewers,
              onChange: (e) => this.handleNonGoldSwitch(e, 'allowPrivateMessagesFromOtherViewers'),
              wrapperClassName: switchWrapper,
            }}
            wrapperClassName={styles.privateChatOptions}
          />
          <SwitchActionItem
            label={translate('v2profile.disable.private.messages.broadcasters')}
            labelClassName={styles.privateChatOptionsSubtitle}
            switchProps={{
              id: this.makeId(this.IDs.disableBroadcasterPm),
              color: 'default',
              checked: Boolean(disablePrivateChat),
              value: disablePrivateChat,
              onChange: (e) => this.handleNonGoldSwitch(e, 'disablePrivateChat'),
              wrapperClassName: switchWrapper,
            }}
            wrapperClassName={styles.privateChatOptions}
          />

          <ChatTranslationPicker
            descriptionClassName={settingsDescription}
            handleLanguageSelectChange={this.handleLanguageSelectChange}
            handleShowOriginalLanguage={this.handleShowOriginalLanguage}
            handleTranslationActiveToggle={this.handleTranslationActiveToggle}
            isGold={isGold}
            languageCode={languageCode}
            showOriginalLanguage={showOriginalLanguage}
            switchWrapperClass={switchWrapper}
            translationSettingsAreActive={translationSettingsAreActive}
            withGoldRestrictions={true}
          />
        </div>
        <div className={languageAndGenderWrapper}>
          <SwitchActionItem
            label={
              <Fragment>
                <img
                  alt="chat settings"
                  className={styles.settingsImg}
                  src={chatSettingsIcon}
                />
                <span>
                  {translate('v2profile.chat.filters')}
                </span>
              </Fragment>
            }
            switchProps={{
              id: this.makeId(this.IDs.chatFilters),
              color: 'default',
              checked: Boolean(enableFilters),
              value: enableFilters,
              onChange: (e) => this.handleChatFiltersSwitch(e, 'enableFilters'),
              wrapperClassName: switchWrapper,
            }}
            wrapperClassName={styles.settingsSubtitle}
          />
          <div className={styles.activeChatFilterWrapper}>
            <GoldStar />
            <span className={settingsDescription}>
              {translate('v2profile.display.following.messages')}
            </span>
            <ReactSelect
              className={styles.selectWrapper}
              clearable={false}
              disabled={!isGold || !enableFilters}
              id={this.makeId(this.IDs.filterLangs)}
              menuContainerStyle={{ top: 'auto', bottom: '100%' }}
              options={chatSettingsLanguageOptions}
              searchable={false}
              value={allowMyLanguage}
              onChange={this.onLangFilterChange}
            />
            <ReactSelect
              clearable={false}
              disabled={!isGold || !enableFilters}
              id={this.makeId(this.IDs.filterGenders)}
              menuContainerStyle={{ top: 'auto', bottom: '100%' }}
              options={chatSettingsGenderOptions}
              searchable={false}
              value={selectedGender}
              onChange={this.onGenderFilterChange}
            />
          </div>
        </div>
        <div className={styles.chatFilterWrapper}>
          <div className={styles.subTitle}>
            {translate('v2profile.room.visibility')}
          </div>
          <div className={styles.activeChatFilterWrapper}>
            <GoldStar className={styles.bottomMg} />
            <ReactSelect
              clearable={false}
              id={this.makeId(this.IDs.visibilityOptions)}
              menuContainerStyle={{ top: 'auto', bottom: '100%' }}
              options={chatSettingsVisibilityOptions}
              searchable={false}
              value={stealthModeValue ? stealthModeValue.split('|')[0] : 'visible'}
              onChange={this.onStealthModeChange}
            />
          </div>
          <SwitchActionItem
            label={translate('v2profile.room.entrance')}
            switchProps={{
              id: this.makeId(this.IDs.roomEntrance),
              checked: isGold && Boolean(notifyIEnterRoom),
              value: notifyIEnterRoom,
              onChange: (e) => this.handleChatFiltersSwitch(e, 'notifyIEnterRoom'),
              wrapperClassName: switchWrapper,
            }}
            wrapperClassName={styles.settingsSubtitle}
          />
          <div className={styles.activeChatFilterWrapper}>
            <GoldStar />
            <span className={styles.notifyBroadcasterDesc}>
              {translate('v2profile.notify.broadcaster.enter.room')}
            </span>
          </div>
        </div>
      </div>
    );
  }
}

export default ChatSettingsV2;
